import { useState, useCallback } from 'react';
import { apiClient } from '../services/api';
import type { ProductDetail, WidgetAuth } from '../types/api';

interface UseProductDetailReturn {
  product: ProductDetail | null;
  isLoading: boolean;
  error: string | null;
  fetchProduct: (productId: number, kind?: string) => Promise<void>;
  reset: () => void;
}

export function useProductDetail(auth: WidgetAuth | null): UseProductDetailReturn {
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProduct = useCallback(async (productId: number, kind?: string) => {
    if (!auth) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.getProductDetail(productId, auth, kind);
      setProduct(response.response);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load product details';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [auth]);

  const reset = useCallback(() => {
    setProduct(null);
    setIsLoading(false);
    setError(null);
  }, []);

  return { product, isLoading, error, fetchProduct, reset };
}
